import React, {useEffect, useMemo, useState} from "react";

const TYPING_SPEED = 100;
const SHOW_TIME = 2000;
const DEL_SPEED = 25;

interface TypeWriterProps {
    text: string;
}

type TypeWriterState = "TYPING" | "BLINK" | "CLEARING";

function TypeWriter({text, className}: TypeWriterProps & React.HTMLProps<HTMLDivElement>) {

    const [display, setDisplay] = useState(" ");
    const [state, setState] = useState<TypeWriterState>("TYPING");
    const [showUnderscore, setShowUnderscore] = useState(true);

    const prefersReducedMotion = useMemo(() => {
        return typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }, []);

    // type the characters one by one
    useEffect(() => {
        if (!text || !text.trim().length || prefersReducedMotion) return;
        let typingTimer: string | number | NodeJS.Timeout | undefined;
        if (state === "TYPING") {
            let idx = 0;
            typingTimer = setInterval(() => {
                if (idx <= text.length) {
                    setDisplay(text.substring(0, idx));
                    idx++;
                } else {
                    // when all characters are typed, after SHOW_TIME set clear flag
                    clearInterval(typingTimer);
                    // console.log('set state blink')
                    setState("BLINK");
                }
            }, TYPING_SPEED);
        }

        return () => {
            clearInterval(typingTimer);
        };
    }, [text, state, prefersReducedMotion]);

    // clear the characters one by one
    useEffect(() => {
        let clearTimer: string | number | NodeJS.Timeout | undefined;
        if (state === "CLEARING") {
            let idx = text.length - 1;
            clearTimer = setInterval(() => {
                if (idx >= 0) {
                    setDisplay(text.substring(0, idx));
                    idx--;
                } else {
                    // when all characters cleared, reset and invoke the callback
                    clearInterval(clearTimer);
                    // console.log('set state typing')
                    setState("TYPING");
                }
            }, DEL_SPEED);
        }
        return () => {
            if (clearTimer) {
                clearInterval(clearTimer);
            }
        };
    }, [state, text]);

    // blink the underscore when idle
    useEffect(() => {
        let blinkTimer: string | number | NodeJS.Timeout | undefined;
        let clearTimer: string | number | NodeJS.Timeout | undefined;
        if (state === "BLINK") {
            // console.log("Start Blinking");
            blinkTimer = setInterval(() => {
                setShowUnderscore(prevShowUnderscore => !prevShowUnderscore);
            }, 500);
            if(!clearTimer)
            clearTimer = setInterval(() => {
                // console.log('set state clearing')
                setState("CLEARING");
                setShowUnderscore(true);
            }, SHOW_TIME);
        }
        return () => {
            // console.log("Stop Blinking");
            if (blinkTimer) {
                clearInterval(blinkTimer);
            }
            if (clearTimer)
                clearInterval(clearTimer);
        };
    }, [state]);

    if (prefersReducedMotion) {
        return <span className={className}>{text}</span>;
    }

    return <span className={className}>{state === "BLINK" && showUnderscore ? display + '_' : display + " "}</span>;
}

export default TypeWriter;
